// FILE: components/Layout.tsx
// Layout composition components for building common page structures

import React from 'react';

// Base layout wrapper
interface LayoutProps {
  children: React.ReactNode;
  className?: string;
}

export const Layout: React.FC<LayoutProps> = ({ children, className = '' }) => {
  return (
    <div className={`min-h-screen flex flex-col ${className}`}>
      {children}
    </div>
  );
};

// Layout Header component (renamed to avoid conflict)
interface LayoutHeaderProps {
  children: React.ReactNode;
  className?: string;
  sticky?: boolean;
  elevated?: boolean;
}

export const LayoutHeader: React.FC<LayoutHeaderProps> = ({ 
  children, 
  className = '', 
  sticky = false,
  elevated = false 
}) => {
  const stickyClass = sticky ? 'sticky top-0 z-50' : '';
  const elevatedClass = elevated ? 'shadow-sm border-b border-border-color dark:border-dark-border-color' : '';
  
  return (
    <header className={`bg-bg-primary dark:bg-dark-bg-primary ${stickyClass} ${elevatedClass} ${className}`}>
      {children}
    </header>
  );
};

// Main content area
interface MainProps {
  children: React.ReactNode;
  className?: string;
  fullWidth?: boolean;
}

export const Main: React.FC<MainProps> = ({ 
  children, 
  className = '', 
  fullWidth = false 
}) => {
  const widthClass = fullWidth ? 'w-full' : 'max-w-7xl mx-auto';
  
  return (
    <main className={`flex-1 ${widthClass} ${className}`}>
      {children}
    </main>
  );
};

// Layout Sidebar component (renamed to avoid conflict)
interface LayoutSidebarProps {
  children: React.ReactNode;
  className?: string;
  width?: 'sm' | 'md' | 'lg' | 'xl';
  position?: 'left' | 'right';
  sticky?: boolean;
}

export const LayoutSidebar: React.FC<LayoutSidebarProps> = ({ 
  children, 
  className = '', 
  width = 'md',
  position = 'left',
  sticky = false 
}) => {
  const widthClasses = {
    sm: 'w-64',
    md: 'w-80',
    lg: 'w-96',
    xl: 'w-[28rem]',
  };
  
  const stickyClass = sticky ? 'sticky top-0 h-screen overflow-y-auto' : '';
  const positionClass = position === 'right' ? 'order-last' : 'order-first';
  
  return (
    <aside className={`${widthClasses[width]} ${stickyClass} ${positionClass} ${className}`}>
      {children}
    </aside>
  );
};

// Footer component
interface FooterProps {
  children: React.ReactNode;
  className?: string;
  elevated?: boolean;
}

export const Footer: React.FC<FooterProps> = ({ 
  children, 
  className = '', 
  elevated = false 
}) => {
  const elevatedClass = elevated ? 'shadow-inner border-t border-border-color dark:border-dark-border-color' : '';
  
  return (
    <footer className={`bg-bg-secondary dark:bg-dark-bg-secondary mt-auto ${elevatedClass} ${className}`}>
      {children}
    </footer>
  );
};

// Content wrapper for main content
interface ContentProps {
  children: React.ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
}

export const Content: React.FC<ContentProps> = ({ 
  children, 
  className = '', 
  padding = 'md' 
}) => {
  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
    xl: 'p-12',
  };
  
  return (
    <div className={`${paddingClasses[padding]} ${className}`}>
      {children}
    </div>
  );
};

// Two-column layout
interface TwoColumnLayoutProps {
  children: React.ReactNode;
  className?: string;
  sidebarPosition?: 'left' | 'right';
  gap?: 'sm' | 'md' | 'lg' | 'xl';
}

export const TwoColumnLayout: React.FC<TwoColumnLayoutProps> = ({ 
  children, 
  className = '', 
  sidebarPosition = 'left',
  gap = 'lg'
}) => {
  const gapClasses = {
    sm: 'gap-4',
    md: 'gap-6',
    lg: 'gap-8',
    xl: 'gap-12',
  };
  
  const [sidebar, main] = React.Children.toArray(children);
  
  return (
    <div className={`flex ${gapClasses[gap]} ${className}`}>
      {sidebarPosition === 'left' ? (
        <>
          {sidebar}
          <div className="flex-1">{main}</div>
        </>
      ) : (
        <>
          <div className="flex-1">{main}</div>
          {sidebar}
        </>
      )}
    </div>
  );
};

// Three-column layout
interface ThreeColumnLayoutProps {
  children: React.ReactNode;
  className?: string;
  gap?: 'sm' | 'md' | 'lg' | 'xl';
}

export const ThreeColumnLayout: React.FC<ThreeColumnLayoutProps> = ({ 
  children, 
  className = '', 
  gap = 'lg'
}) => {
  const gapClasses = {
    sm: 'gap-4',
    md: 'gap-6',
    lg: 'gap-8',
    xl: 'gap-12',
  };
  
  const [leftSidebar, main, rightSidebar] = React.Children.toArray(children);
  
  return (
    <div className={`flex ${gapClasses[gap]} ${className}`}>
      {leftSidebar}
      <div className="flex-1">{main}</div>
      {rightSidebar}
    </div>
  );
};
